import { CancelTokenSource } from 'axios';
import { useEffect, useRef, useState } from 'react';

import { AuthToken } from '@services/auth-token/authToken';
import { generateNewCancelTokenSource } from '@services/axios/axiosUtils';
import { BackOfficeAPIClient } from '@services/axios/clients/backOfficeAPIClient';
import { PlanItemPayload } from '@shared/interfaces/backoffice';

interface PlansPayload {
    count: number;
    data: PlanItemPayload[];
    success: boolean;
}

export default function usePaymentPlans({ fetchOnInit = true, fetchActiveOnInit = false, }) {
    const [isLoading, setIsLoading]           = useState(false);
    const [hasError, setHasError]             = useState(false);
    const [activeHasError, setActiveHasError] = useState(false);
    const [plans, setPlans]                   = useState<PlanItemPayload[]>([]);
    const [activePlan, setActivePlan]         = useState<PlanItemPayload>(null);
    const tokenSource                         = useRef<CancelTokenSource>(null);
    const activeTokenSource                   = useRef<CancelTokenSource>(null);

    useEffect(() => {
        if (fetchOnInit) {
            fetchPlans();
        }

        if (fetchActiveOnInit) {
            fetchActivePlan();
        }
    }, []);

    const fetchPlans = async () => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const response = await BackOfficeAPIClient.getPlans<PlansPayload>({
                cancelToken: tokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setPlans(response?.data?.data || []);
                setHasError(false);

                return response.data;
            }

            throw new Error('Failed to fetch plans!');
        } catch (e) {
            console.error(e);
            setHasError(true);

            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchActivePlan = async () => {
        setIsLoading(true);

        if (activeTokenSource.current) {
            activeTokenSource.current.cancel();
        }

        activeTokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response = await BackOfficeAPIClient.getActiveOrder<PlanItemPayload>(clientId, {
                cancelToken: activeTokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setActivePlan(response?.data);
                setActiveHasError(false);

                return response.data;
            }

            throw new Error('Failed to fetch active plan!');
        } catch (e) {
            console.error(e);
            setActiveHasError(true);

            return null;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        hasError,
        plans,
        fetchPlans,
        activeHasError,
        activePlan,
        fetchActivePlan,
    };
}
